import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { MdCheck, MdCopyAll } from "react-icons/md";

const OperationDocPage = () => {
    const { doc_page } = useParams();
    const [selectedLanguage, setSelectedLanguage] = useState("javascript");
    const [copySuccess, setCopySuccess] = useState(false);

    const codeSnippets = {
        insert_one: {
            description: "Insert a single document into a collection by sending a POST request. Provide the data fields in JSON format in the body. The server will save it and return a confirmation.",
            javascript: `// JavaScript Example
fetch('https://api.erioon.com/YOUR_USER_ID/PROJECT_ID/DATABASE_ID/REGION/COLLECTION_ID/insert_one', {
    method: 'POST',
    headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer YOUR_API_KEY'
    },
    body: JSON.stringify({ name: "John", surname: "Doe", age: 30 })
})
.then(res => res.json())
.then(data => console.log(data));`,
            python: `# Python Example
import requests

url = "https://api.erioon.com/YOUR_USER_ID/PROJECT_ID/DATABASE_ID/REGION/COLLECTION_ID/insert_one"
data = { "name": "John", "surname": "Doe", "age": 30 }
headers = { "Authorization": "Bearer YOUR_API_KEY" }

response = requests.post(url, json=data, headers=headers)
print(response.json())`,
            curl: `# cURL Example
curl -X POST https://api.erioon.com/YOUR_USER_ID/PROJECT_ID/DATABASE_ID/REGION/COLLECTION_ID/insert_one \\
-H "Content-Type: application/json" \\
-H "Authorization: Bearer YOUR_API_KEY" \\
-d '{"name": "John", "surname": "Doe", "age": 30}'`
        },

        insert_many: {
            description: "Insert multiple documents in a single POST request. You must wrap the documents inside a 'records' field.",
            javascript: `// JavaScript Example
fetch('https://api.erioon.com/YOUR_USER_ID/PROJECT_ID/DATABASE_ID/REGION/COLLECTION_ID/insert_many', {
    method: 'POST',
    headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer YOUR_API_KEY'
    },
    body: JSON.stringify({
        records: [
            { name: "John", surname: "Doe", age: 30 },
            { name: "Jack", surname: "Sparrow", age: 28 }
        ]
    })
})
.then(res => res.json())
.then(data => console.log(data));`,
            python: `# Python Example
import requests

url = "https://api.erioon.com/YOUR_USER_ID/PROJECT_ID/DATABASE_ID/REGION/COLLECTION_ID/insert_many"
data = {
    "records": [
        { "name": "John", "surname": "Doe", "age": 30 },
        { "name": "Jack", "surname": "Sparrow", "age": 28 }
    ]
}
headers = { "Authorization": "Bearer YOUR_API_KEY" }

response = requests.post(url, json=data, headers=headers)
print(response.json())`,
            curl: `# cURL Example
curl -X POST https://api.erioon.com/YOUR_USER_ID/PROJECT_ID/DATABASE_ID/REGION/COLLECTION_ID/insert_many \\
-H "Content-Type: application/json" \\
-H "Authorization: Bearer YOUR_API_KEY" \\
-d '{"records": [{"name": "John", "surname": "Doe", "age": 30}, {"name": "Jack", "surname": "Sparrow", "age": 28}]}'`
        },

        get_all: {
            description: "Fetch all documents from a collection using a simple GET request. No body parameters are needed, just authorization.",
            javascript: `// JavaScript Example
fetch('https://api.erioon.com/YOUR_USER_ID/PROJECT_ID/DATABASE_ID/REGION/COLLECTION_ID/get_all', {
    method: 'GET',
    headers: {
        'Authorization': 'Bearer YOUR_API_KEY'
    }
})
.then(res => res.json())
.then(data => console.log(data));`,
            python: `# Python Example
import requests

url = "https://api.erioon.com/YOUR_USER_ID/PROJECT_ID/DATABASE_ID/REGION/COLLECTION_ID/get_all"
headers = { "Authorization": "Bearer YOUR_API_KEY" }

response = requests.get(url, headers=headers)
print(response.json())`,
            curl: `# cURL Example
curl -X GET https://api.erioon.com/YOUR_USER_ID/PROJECT_ID/DATABASE_ID/REGION/COLLECTION_ID/get_all \\
-H "Authorization: Bearer YOUR_API_KEY"`
        },

        get_specific: {
            description: "Retrieve a specific document by providing query parameters (e.g., _id). Use a GET request with URL parameters.",
            javascript: `// JavaScript Example
fetch('https://api.erioon.com/YOUR_USER_ID/PROJECT_ID/DATABASE_ID/REGION/COLLECTION_ID/get_specific?_id=DOCUMENT_ID', {
    method: 'GET',
    headers: {
        'Authorization': 'Bearer YOUR_API_KEY'
    }
})
.then(res => res.json())
.then(data => console.log(data));`,
            python: `# Python Example
import requests

url = "https://api.erioon.com/YOUR_USER_ID/PROJECT_ID/DATABASE_ID/REGION/COLLECTION_ID/get_specific"
params = { "_id": "DOCUMENT_ID" }
headers = { "Authorization": "Bearer YOUR_API_KEY" }

response = requests.get(url, params=params, headers=headers)
print(response.json())`,
            curl: `# cURL Example
curl -G https://api.erioon.com/YOUR_USER_ID/PROJECT_ID/DATABASE_ID/REGION/COLLECTION_ID/get_specific \\
-H "Authorization: Bearer YOUR_API_KEY" \\
--data-urlencode "_id=DOCUMENT_ID"`
        },

        delete_one: {
            description: "Delete a single document based on a filter (typically _id). Use a DELETE request with JSON body.",
            javascript: `// JavaScript Example
fetch('https://api.erioon.com/YOUR_USER_ID/PROJECT_ID/DATABASE_ID/REGION/COLLECTION_ID/delete_one', {
    method: 'DELETE',
    headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer YOUR_API_KEY'
    },
    body: JSON.stringify({ _id: "DOCUMENT_ID" })
})
.then(res => res.json())
.then(data => console.log(data));`,
            python: `# Python Example
import requests

url = "https://api.erioon.com/YOUR_USER_ID/PROJECT_ID/DATABASE_ID/REGION/COLLECTION_ID/delete_one"
data = { "_id": "DOCUMENT_ID" }
headers = { "Authorization": "Bearer YOUR_API_KEY" }

response = requests.delete(url, json=data, headers=headers)
print(response.json())`,
            curl: `# cURL Example
curl -X DELETE https://api.erioon.com/YOUR_USER_ID/PROJECT_ID/DATABASE_ID/REGION/COLLECTION_ID/delete_one \\
-H "Content-Type: application/json" \\
-H "Authorization: Bearer YOUR_API_KEY" \\
-d '{"_id": "DOCUMENT_ID"}'`
        },

        delete_many: {
            description: "Delete multiple documents that match given filters. Use a DELETE request with an array of JSON filters.",
            javascript: `// JavaScript Example
fetch('https://api.erioon.com/YOUR_USER_ID/PROJECT_ID/DATABASE_ID/REGION/COLLECTION_ID/delete_many', {
    method: 'DELETE',
    headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer YOUR_API_KEY'
    },
    body: JSON.stringify([
        { "name": "Mario" },
        { "surname": "Rossi" }
    ])
})
.then(res => res.json())
.then(data => console.log(data));`,
            python: `# Python Example
import requests

url = "https://api.erioon.com/YOUR_USER_ID/PROJECT_ID/DATABASE_ID/REGION/COLLECTION_ID/delete_many"
data = [
    { "name": "Mario" },
    { "surname": "Rossi" }
]
headers = { "Authorization": "Bearer YOUR_API_KEY" }

response = requests.delete(url, json=data, headers=headers)
print(response.json())`,
            curl: `# cURL Example
curl -X DELETE https://api.erioon.com/YOUR_USER_ID/PROJECT_ID/DATABASE_ID/REGION/COLLECTION_ID/delete_many \\
-H "Content-Type: application/json" \\
-H "Authorization: Bearer YOUR_API_KEY" \\
-d '[{"name": "Mario"}, {"surname": "Rossi"}]'`
        },

        update_query: {
            description: "Update one or more documents by sending a filter and update object via PATCH.",
            javascript: `// JavaScript Example
fetch('https://api.erioon.com/YOUR_USER_ID/PROJECT_ID/DATABASE_ID/REGION/COLLECTION_ID/update_query', {
    method: 'PATCH',
    headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer YOUR_API_KEY'
    },
    body: JSON.stringify({
        filter_query: { "_id": "DOCUMENT_ID" },
        update_query: { "$set": { "address": "123 New Street, Cityville" } }
    })
})
.then(res => res.json())
.then(data => console.log(data));`,
            python: `# Python Example
import requests

url = "https://api.erioon.com/YOUR_USER_ID/PROJECT_ID/DATABASE_ID/REGION/COLLECTION_ID/update_query"
data = {
    "filter_query": { "_id": "DOCUMENT_ID" },
    "update_query": { "$set": { "address": "123 New Street, Cityville" } }
}
headers = { "Authorization": "Bearer YOUR_API_KEY" }

response = requests.patch(url, json=data, headers=headers)
print(response.json())`,
            curl: `# cURL Example
curl -X PATCH https://api.erioon.com/YOUR_USER_ID/PROJECT_ID/DATABASE_ID/REGION/COLLECTION_ID/update_query \\
-H "Content-Type: application/json" \\
-H "Authorization: Bearer YOUR_API_KEY" \\
-d '{"filter_query": {"_id": "DOCUMENT_ID"}, "update_query": {"$set": {"address": "123 New Street, Cityville"}}}'`
        },

        // Python SDK Methods
        py_insert_one: {
            description: "Insert a single document using the Python SDK with the `insert_one()` method.",
            sdk: `from erioon.login import ErioonLogin

api_key = "your_api_key"
username = "your_username"
password = "your_password"

client = ErioonLogin(f"{api_key}:{username}:{password}")
db = client["your_database_id"]
col = db["your_collection_id"]

data = { "name": "John", "age": 30 }
result = col.insert_one(data)
print(result)`
        },

        py_insert_many: {
            description: "Insert multiple documents using `insert_many()` by passing a list of dictionaries.",
            sdk: `from erioon.login import ErioonLogin

api_key = "your_api_key"
username = "your_username"
password = "your_password"

client = ErioonLogin(f"{api_key}:{username}:{password}")
db = client["your_database_id"]
col = db["your_collection_id"]

data = {   
    "records" : [
        { "name": "Alice", "age": 28 },
        { "name": "Bob", "age": 35 }
    ]
}


result = col.insert_many(records)
print(result)`
        },


        py_get_all: {
            description: "Retrieve all documents from a collection using the `get_all()` method.",
            sdk: `from erioon.login import ErioonLogin

api_key = "your_api_key"
username = "your_username"
password = "your_password"

client = ErioonLogin(f"{api_key}:{username}:{password}")
db = client["your_database_id"]
col = db["your_collection_id"]

documents = col.get_all()
for doc in documents:
    print(doc)`
        },


        py_get_specific: {
            description: "Retrieve documents using a filter dictionary with the `get_specific()` method.",
            sdk: `from erioon.login import ErioonLogin

api_key = "your_api_key"
username = "your_username"
password = "your_password"

client = ErioonLogin(f"{api_key}:{username}:{password}")
db = client["your_database_id"]
col = db["your_collection_id"]

query = { "name": "John" }
documents = col.get_specific(query)
print(documents)`
        },


        py_delete_one: {
            description: "Delete a single document matching a filter using `delete_one()`.",
            sdk: `from erioon.login import ErioonLogin

api_key = "your_api_key"
username = "your_username"
password = "your_password"

client = ErioonLogin(f"{api_key}:{username}:{password}")
db = client["your_database_id"]
col = db["your_collection_id"]

result = col.delete_one({ "_id": "document_id" })
print(result)`
        },


        py_delete_many: {
            description: "Delete multiple documents using a list of filters with `delete_many()`.",
            sdk: `from erioon.login import ErioonLogin

api_key = "your_api_key"
username = "your_username"
password = "your_password"

client = ErioonLogin(f"{api_key}:{username}:{password}")
db = client["your_database_id"]
col = db["your_collection_id"]

filters = [
    { "name": "Alice" },
    { "age": 35 }
]

result = col.delete_many(filters)
print(result)`
        },


        py_update_query: {
            description: "Update one or more documents using `update_query()` with filter and update objects.",
            sdk: `from erioon.login import ErioonLogin

api_key = "your_api_key"
username = "your_username"
password = "your_password"

client = ErioonLogin(f"{api_key}:{username}:{password}")
db = client["your_database_id"]
col = db["your_collection_id"]

filter_query = { "_id": "document_id" }
update_query = { "$set": { "email": "new@email.com" } }

result = col.update_query(filter_query, update_query)
print(result)`
        },

    };


    const operationData = codeSnippets[doc_page];

    if (!operationData) {
        return (
            <div className="doc_page_cont">
                <p className="doc_page_cont_title">Page not Found</p>
                <p className="doc_page_cont_paragraph">The page you're looking for does not exist.</p>
            </div>
        );
    }

    const copyToClipboard = () => {
        const codeSnippet = operationData[selectedLanguage];
        navigator.clipboard.writeText(codeSnippet).then(
            () => {
                setCopySuccess(true);
                setTimeout(() => setCopySuccess(false), 2000);
            },
            (err) => {
                console.error("Error copying text: ", err);
            }
        );
    };

    return (
        <div className="doc_page_cont">
            <p className="doc_page_cont_title">
                The <code>{doc_page}</code> function
            </p>

            <div className="crud_ops_cont">
                <div className="crud_ops_cont_desc">
                    <p className="doc_page_cont_paragraph_title">How does it work?</p>
                    <p className="doc_page_cont_paragraph_desc">
                        {operationData.description}
                    </p>
                    <div className="doc_page_cont_paragraph_hint">
                        <p className="doc_page_cont_paragraph_title">Hint</p>
                        <p className="hint_parag_desc_docu">
                            Make sure to substitute USER_ID, PROJECT_ID, DATABASE_ID, REGION and COLLECTION_ID with the actual IDs. <br />
                            You can find the complete URL in "Test API connection example"
                        </p>
                    </div>
                </div>

                {operationData.sdk ? (
                    // --- SDK version ---
                    <div className="code_snippet_docs">
                        <pre className="line-numbers">
                            <code className="language-python">{operationData.sdk}</code>
                        </pre>
                    </div>
                ) : (
                    // --- REST version with language switcher ---
                    <div className="button_code_container">
                        <div className="button_code_cont">
                            {["javascript", "python", "curl"].map((lang) =>
                                operationData[lang] ? (
                                    <button
                                        key={lang}
                                        onClick={() => setSelectedLanguage(lang)}
                                        className={`doc_btn ${selectedLanguage === lang ? "selected_doc_btn" : ""}`}
                                    >
                                        {lang.charAt(0).toUpperCase() + lang.slice(1)}
                                    </button>
                                ) : null
                            )}
                            <div className="copy_doc_cont">
                                <button className="copy_btn_docu_btn" onClick={copyToClipboard}>
                                    {copySuccess ? <MdCheck /> : <MdCopyAll />}
                                </button>
                            </div>
                        </div>
                        <div className="code_snippet_docs">
                            <pre className="line-numbers">
                                <code className={`language-${selectedLanguage}`}>
                                    {operationData[selectedLanguage]}
                                </code>
                            </pre>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

};

export default OperationDocPage;
